import { Dimensions, DisplayUnits } from './dimensions';

export interface IGrid {
  class: string;
  display: string;
  icon: string;
  iconColor: string;
}

export const GRIDS: IGrid[] = [
  {
    class: 'red-grid',
    display: 'Red Grid',
    icon: 'grid',
    iconColor: 'primary',
  },
  {
    class: 'black-grid',
    display: 'Black Grid',
    icon: 'grid',
    iconColor: 'black',
  },
  {
    class: 'white-grid',
    display: 'White Grid',
    icon: 'grid',
    iconColor: 'light',
  },
  {
    class: 'grey-grid',
    display: 'Grey Grid',
    icon: 'grid',
    iconColor: 'medium',
  },
  {
    class: 'green-grid',
    display: 'Green Grid',
    icon: 'grid',
    iconColor: 'success',
  },
  {
    class: 'orange-grid',
    display: 'Orange Grid',
    icon: 'grid',
    iconColor: 'secondary',
  },
  {
    class: 'yellow-grid',
    display: 'Yellow Grid',
    icon: 'grid',
    iconColor: 'warning',
  },
  {
    class: 'blue-grid',
    display: 'Blue Grid',
    icon: 'grid',
    iconColor: 'info',
  },
  {
    class: '',
    display: 'None',
    icon: 'square-outline',
    iconColor: 'primary',
  },
];

export interface ISettings {
  crossHairColor?: string;
  crossHairScale?: number;
  fontScale?: number;
  gridTheme?: IGrid;
  hasSeenLayoutViewHelp?: boolean;
  hasSeenLayoutVisualEditHelp?: boolean;
  hasSeenMapDesignViewHelp?: boolean;
  hasSeenTutorial?: boolean;
  layoutFavorites?: string[];
  lineThickness?: number;
  mapFavorites?: string[];
  obstacleColor?: string;
  pointColor?: string;
  pointRadius?: number;
  releaseNotesVersionSeen?: string;
  showCoordinates?: boolean;
  showGrid?: boolean;
  showNumbers?: boolean;
  showOrigin?: boolean;
  displayUnits?: DisplayUnits;
}

export class Settings implements ISettings {
  crossHairColor = 'primary';
  crossHairScale = 1;
  fontScale = 1;
  gridTheme: IGrid = GRIDS[0];
  hasSeenLayoutViewHelp = false;
  hasSeenLayoutVisualEditHelp = false;
  hasSeenMapDesignViewHelp = false;
  hasSeenTutorial = false;
  layoutFavorites: string[] = [];
  lineThickness = 1;
  mapFavorites: string[] = [];
  obstacleColor = 'primary';
  pointColor = 'medium';
  pointRadius = 1;
  releaseNotesVersionSeen = '';
  showCoordinates = true;
  showGrid = true;
  showNumbers = true;
  showOrigin = false;
  units: DisplayUnits = DisplayUnits.feet;
  displayUnits: DisplayUnits = DisplayUnits.feet;

  constructor({
    crossHairColor = 'primary',
    crossHairScale = 1,
    fontScale = 1,
    gridTheme = GRIDS[0],
    hasSeenLayoutViewHelp = false,
    hasSeenLayoutVisualEditHelp = false,
    hasSeenMapDesignViewHelp = false,
    hasSeenTutorial = false,
    layoutFavorites = [],
    lineThickness = 1,
    mapFavorites = [],
    obstacleColor = 'primary',
    pointColor = 'medium',
    pointRadius = 1,
    releaseNotesVersionSeen = '',
    showCoordinates = true,
    showGrid = true,
    showNumbers = true,
    showOrigin = false,
    units = DisplayUnits.feet, // now obsolete, use displayUnits
    displayUnits = DisplayUnits.feet,
    ...rest
  }: ISettings & { displayUnits?: DisplayUnits; units?: DisplayUnits }) {
    this.gridTheme = gridTheme;
    if (typeof this.gridTheme === 'string') {
      this.gridTheme = GRIDS[0];
    }
    this.crossHairColor = crossHairColor;
    this.crossHairScale = crossHairScale;
    this.fontScale = fontScale;
    this.hasSeenLayoutViewHelp = hasSeenLayoutViewHelp;
    this.hasSeenLayoutVisualEditHelp = hasSeenLayoutVisualEditHelp;
    this.hasSeenMapDesignViewHelp = hasSeenMapDesignViewHelp;
    this.hasSeenTutorial = hasSeenTutorial;
    this.layoutFavorites = layoutFavorites;
    this.lineThickness = lineThickness;
    this.mapFavorites = mapFavorites;
    this.obstacleColor = obstacleColor;
    this.pointColor = pointColor;
    this.pointRadius = pointRadius;
    this.releaseNotesVersionSeen = releaseNotesVersionSeen;
    this.showCoordinates = showCoordinates;
    this.showGrid = showGrid;
    this.showNumbers = showNumbers;
    this.showOrigin = showOrigin;
    this.displayUnits = displayUnits ?? units ?? DisplayUnits.feet;

    Object.assign(this, rest); // allow old source to edit/persist properties from future versions
  }

  displayGrid(): string {
    return (this.showGrid && 'True') || 'False';
  }

  displayGridTheme(): string {
    return this.gridTheme.display;
  }

  displayNumbers(): string {
    return (this.showNumbers && 'True') || 'False';
  }

  displayShowOrigin(): string {
    return (this.showOrigin && 'True') || 'False';
  }

  displayUnitName(): string {
    return Dimensions.displayUnitNames(this.displayUnits);
  }

  static setToDefaults(settings: ISettings): ISettings {
    settings.crossHairColor = 'primary';
    settings.crossHairScale = 1;
    settings.fontScale = 1;
    settings.gridTheme = GRIDS[0];
    settings.lineThickness = 1;
    settings.pointColor = 'medium';
    settings.pointRadius = 1;
    settings.showCoordinates = true;
    settings.showGrid = true;
    settings.showNumbers = true;
    settings.showOrigin = false;
    settings.displayUnits = DisplayUnits.feet;
    settings.obstacleColor = 'primary';
    return settings;
  }
}
